import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { lookbookApi } from "@/api/lookbook-api";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  HtmlDocumentFrame,
  htmlPayloadOf,
} from "@/components/common/html-document-frame";
import { GenUIRenderer } from "@/components/genui/genui-renderer";
import {
  LinkedInCompanyReport,
  type LinkedInCompanyReportData,
} from "@/components/reports/linkedin-company-report";
import { lookbookReportMeta } from "@/lib/lookbook-reports";
import { cn } from "@/lib/utils";
import {
  dedupeByTitle,
  extractUiData,
  extractMetrics,
  extractPayload,
  formatScalar,
  hasLinkedInReportContent,
  humanize,
  imageUrl,
  isEmptyValue,
  isScalar,
  metricValue,
  HIDDEN_KEYS,
  type MetricItem,
} from "./lookbook-report-payload";
import { RBCard, SectionLabel } from "./rb-ui";

interface LookbookReportsProps {
  id?: string;
  reportKeys: string[];
  publicId: string;
  companyId: string;
}

/**
 * The reports a lookbook shares for one deal.
 *
 * Read-only by construction: no generate, no delete, no version dropdown, no
 * premium gate. The internal report components can't be reused — every one of
 * them reaches for the workspace store, the current-deal store and aiApi — so
 * this renders the payload directly, and only the pieces of it that are
 * presentational.
 *
 * Bodies are fetched when a report is expanded rather than with the board
 * payload: reports are large, and the board payload is shared with the index
 * page's cache entry.
 */
export function LookbookReports({
  id,
  reportKeys,
  publicId,
  companyId,
}: LookbookReportsProps) {
  // The catalog lists tearsheets and tearsheets_v2 separately because they are
  // stored differently; to a reader they are one report, so collapse by title.
  const shown = dedupeByTitle(reportKeys);

  return (
    <RBCard id={id}>
      <SectionLabel>Reports</SectionLabel>

      <div className="mt-4 space-y-2">
        {shown.map((key, index) => (
          <ReportRow
            key={key}
            reportKey={key}
            publicId={publicId}
            companyId={companyId}
            defaultOpen={index === 0}
          />
        ))}
      </div>
    </RBCard>
  );
}

function ReportRow({
  reportKey,
  publicId,
  companyId,
  defaultOpen,
}: {
  reportKey: string;
  publicId: string;
  companyId: string;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const meta = lookbookReportMeta(reportKey);
  const Icon = meta.icon;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["lookbook-report", publicId, companyId, reportKey],
    queryFn: () =>
      lookbookApi.getSharedReport(publicId, companyId, reportKey),
    enabled: open,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const item = (data?.items ?? [])[0] as Record<string, unknown> | undefined;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border border-border/60"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{meta.title}</p>
          {meta.description && (
            <p className="truncate text-xs text-muted-foreground">
              {meta.description}
            </p>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="border-t border-border/60 px-4 py-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading {meta.title.toLowerCase()}…
          </div>
        ) : isError || !item ? (
          <p className="text-sm text-muted-foreground">
            This report isn't available.
          </p>
        ) : (
          <ReportBody reportKey={reportKey} item={item} />
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Renders whichever shape the report came back in.
 *
 * These are storage shapes, not a normalized contract, and only two of them are
 * special enough to be worth their own renderer: tearsheets_v2 carries a laid
 * out document, metrics carries category/value rows. Everything else is a nested
 * object of prose, lists and scores, so it goes through one structural renderer
 * rather than a bespoke component per report — a report type added upstream
 * then renders as headed sections instead of as an apology.
 */
function ReportBody({
  reportKey,
  item,
}: {
  reportKey: string;
  item: Record<string, unknown>;
}) {
  if (reportKey === "tearsheets_v2") {
    const uiData = extractUiData(item);
    // Newer tearsheets are a standalone HTML document, not a schema tree.
    // GenUIRenderer reads `.type` off whatever it is handed, so an HTML payload
    // reaching it renders as "Unknown node type: undefined" — which is what a
    // reader saw for every tearsheet generated since the worker switched.
    const html = htmlPayloadOf(uiData);
    if (html) return <HtmlDocumentFrame html={html} title="Tearsheet" />;
    if (uiData) return <GenUIRenderer schema={uiData} />;
  }

  if (reportKey === "metrics") {
    const metrics = extractMetrics(item);
    if (metrics.length > 0) return <MetricsReport metrics={metrics} />;
  }

  if (reportKey === "linkedInInfo") {
    // A scrape is a fixed record, not prose, and the deal page already has a
    // renderer for it — so the public page uses that one rather than walking the
    // stored object. The structural renderer showed every key it found, which
    // meant `text_html` (LinkedIn's own post markup, printed as tags),
    // `unformatted_about` next to `about`, and `Input`/`Company Id` under
    // headings of their own.
    const data = item.data as LinkedInCompanyReportData | undefined;
    if (hasLinkedInReportContent(data)) {
      return <LinkedInCompanyReport data={data!} />;
    }
  }

  const payload = extractPayload(reportKey, item);

  if (isEmptyValue(payload)) {
    return (
      <p className="text-sm text-muted-foreground">
        This report is empty.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <StructuredValue value={payload} depth={0} />
    </div>
  );
}

/**
 * Metrics, grouped under their category.
 *
 * Note what is not read: `sources`, which carries the S3 URL of the document a
 * figure was extracted from. The server strips it, and this never reaches for
 * it either — a public page must not become a way around the document gate.
 */
function MetricsReport({ metrics }: { metrics: MetricItem[] }) {
  const groups = new Map<string, MetricItem[]>();
  for (const metric of metrics) {
    const category = metric?.category || "Metrics";
    const existing = groups.get(category);
    if (existing) existing.push(metric);
    else groups.set(category, [metric]);
  }

  return (
    <div className="space-y-5">
      {[...groups.entries()].map(([category, items]) => (
        <div key={category}>
          <SectionLabel as="h3">{humanize(category)}</SectionLabel>
          <dl className="mt-2 divide-y divide-border/40">
            {items.map((metric, index) => (
              <div
                key={`${metric?.title ?? "metric"}-${index}`}
                className="flex items-baseline justify-between gap-4 py-1.5"
              >
                <dt className="min-w-0 text-sm text-muted-foreground">
                  {humanize(metric?.title ?? "—")}
                  {metric?.sub_category && (
                    <span className="ml-1.5 text-xs opacity-70">
                      {humanize(metric.sub_category)}
                    </span>
                  )}
                </dt>
                <dd className="shrink-0 text-sm font-medium tabular-nums">
                  {metricValue(metric)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Structural renderer                                                       */
/* -------------------------------------------------------------------------- */

const MAX_DEPTH = 6;

function StructuredValue({
  value,
  depth,
  label,
}: {
  value: unknown;
  depth: number;
  /** The field this value sits under, for an image's alt text. */
  label?: string;
}): React.ReactElement | null {
  if (isEmptyValue(value)) return null;

  if (typeof value === "string") {
    const image = imageUrl(value);
    if (image) return <ReportImage src={image} alt={label ?? "Image"} />;

    return (
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown>{value}</ReactMarkdown>
      </div>
    );
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return <p className="text-sm">{formatScalar(value)}</p>;
  }

  if (depth >= MAX_DEPTH) {
    return <p className="text-sm">{formatScalar(value)}</p>;
  }

  if (Array.isArray(value)) {
    const items = value.filter((v) => !isEmptyValue(v));
    if (items.length === 0) return null;

    // A post's attachments, a gallery — a bulleted list of urls is not what
    // this is.
    if (items.every((entry) => imageUrl(entry))) {
      return (
        <div className="flex flex-wrap gap-2">
          {items.map((entry, index) => (
            <ReportImage
              key={index}
              src={imageUrl(entry)!}
              alt={label ? `${label} ${index + 1}` : `Image ${index + 1}`}
            />
          ))}
        </div>
      );
    }

    if (items.every(isScalar)) {
      return (
        <ul className="list-disc space-y-1 pl-5 text-sm marker:text-muted-foreground">
          {items.map((entry, index) => (
            <li key={index}>{formatScalar(entry)}</li>
          ))}
        </ul>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((entry, index) => (
          <div
            key={index}
            className={cn(
              index > 0 && "border-t border-border/40 pt-3",
              "space-y-2"
            )}
          >
            <StructuredValue value={entry} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  const record = value as Record<string, unknown>;

  // `{ generation, sources }` — a summary section with its provenance attached.
  // The prose is the part that is publishable.
  if (typeof record.generation === "string") {
    return <StructuredValue value={record.generation} depth={depth} />;
  }

  // `{ Score, Reason }` — every scored category across startupscore and the
  // scores nested inside a valuation.
  if (typeof record.Score === "number") {
    return <ScoredValue score={record.Score} reason={record.Reason} />;
  }

  const fields = Object.entries(record).filter(
    ([key, entry]) => !HIDDEN_KEYS.has(key) && !isEmptyValue(entry)
  );
  if (fields.length === 0) return null;

  return (
    <div className={cn(depth === 0 ? "space-y-4" : "space-y-2.5")}>
      {fields.map(([key, entry]) => (
        <div key={key} className="space-y-1">
          {/* No heading over a picture: "Img" above an employee's face reads as
              debug output, and an image that fails to load then leaves a
              labelled empty box rather than nothing. */}
          {!imageUrl(entry) && (
            <SectionLabel as="h3" className={cn(depth > 0 && "opacity-80")}>
              {humanize(key)}
            </SectionLabel>
          )}
          <StructuredValue
            value={entry}
            depth={depth + 1}
            label={humanize(key)}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * An image from a report payload.
 *
 * `no-referrer` because the address bar is a private lookbook link and the host
 * is a third party — LinkedIn's CDN has no business being told which board is
 * looking at it. Removed on error rather than left as a broken-image icon:
 * scraped CDN urls expire, and this page has no way to refresh one.
 */
function ReportImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className="max-h-24 max-w-full rounded-md border border-border/60 object-contain"
    />
  );
}

function ScoredValue({ score, reason }: { score: number; reason: unknown }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium tabular-nums">{formatScalar(score)}</p>
      {typeof reason === "string" && reason.trim().length > 0 && (
        <p className="text-sm text-muted-foreground">{reason}</p>
      )}
    </div>
  );
}
